/**
 * federation-status-stream — EventSource-Client fuer den Backend-Health-Watchdog.
 *
 * Verbindet sich mit `/api/portal/federation-status/stream` und reagiert live
 * auf Container-Ausfaelle / -Wiederkehr:
 *
 *  - Server-Event `snapshot`  → Vollstaendige Status-Map, alle Slugs syncen.
 *  - Server-Event `status`    → Delta { slug, status }.
 *
 * Fuer `status = 'up'`:
 *    * registry.retryRemote(slug) — laed das Modul (Sidebar/Router registrieren)
 *    * Bei Erfolg feuert registerModule() intern `federation-status-change`
 *      und `federation-module-registered` — Sidebar-Punkt gruen, Consumer laden.
 *
 * Fuer `status = 'down'`:
 *    * registry.markRemoteDown(slug) — Sidebar-Punkt rot + Retry-Loop reaktivieren
 *    * useFederatedModule-Cache fuer den Slug purgen — Consumer verliert
 *      alte Komponente sofort und zeigt Offline-UI, statt weiter gerendert
 *      zu bleiben (moegliche stale-Komponente nach Container-Ausfall).
 *
 * Auto-Reconnect via EventSource-Default. Kein Cleanup noetig — die Verbindung
 * lebt fuer die gesamte Portal-Session. Doppel-Start wird geschluckt.
 */
import { getModuleStatus, markRemoteDown, retryRemote, type ModuleStatus } from '../modules/registry';
import { invalidateFederatedModulesForSlug } from './useFederatedModule';

type StatusSnapshot = Record<string, ModuleStatus>;
type StatusDelta = { slug: string; status: ModuleStatus; changedAt?: string };

let source: EventSource | null = null;

function applyStatus(slug: string, next: ModuleStatus): void {
  const prev = getModuleStatus(slug);
  if (prev === next) return;
  if (next === 'up') {
    // Modul ist wieder erreichbar — Manifest neu laden (falls es beim
    // Portal-Boot noch nicht da war oder zwischenzeitlich raus flog).
    void retryRemote(slug);
  } else {
    // Ausfall — Cache purgen damit Consumer sofort auf Offline-UI wechselt.
    invalidateFederatedModulesForSlug(slug);
    markRemoteDown(slug);
  }
}

function handleSnapshot(data: StatusSnapshot): void {
  for (const [slug, status] of Object.entries(data)) {
    applyStatus(slug, status);
  }
}

function handleDelta(evt: StatusDelta): void {
  if (!evt?.slug || !evt?.status) return;
  applyStatus(evt.slug, evt.status);
}

/** Stream einmalig starten. Idempotent. */
export function startFederationStatusStream(): void {
  if (source) return;
  if (typeof window === 'undefined' || typeof EventSource === 'undefined') return;

  try {
    source = new EventSource('/api/portal/federation-status/stream', {
      withCredentials: true,
    });
  } catch (err) {
    console.warn('[FederationStatusStream] EventSource-Init fehlgeschlagen:', (err as Error).message);
    source = null;
    return;
  }

  source.addEventListener('snapshot', (e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data) as StatusSnapshot;
      handleSnapshot(data);
    } catch (err) {
      console.warn('[FederationStatusStream] snapshot parse:', (err as Error).message);
    }
  });

  source.addEventListener('status', (e: MessageEvent) => {
    try {
      const data = JSON.parse(e.data) as StatusDelta;
      handleDelta(data);
    } catch (err) {
      console.warn('[FederationStatusStream] status parse:', (err as Error).message);
    }
  });

  source.onerror = () => {
    // EventSource re-connectet automatisch. Nur bei permanentem Fail
    // (401 nach Session-Ablauf, 404) waere manuelles Handling noetig —
    // dann feuert der onerror in Endlos-Schleife, was wir loggen, aber
    // nicht abbrechen (Login-Redirect erledigt der Rest der App).
  };
}

/** Stream stoppen (Logout, Tests). */
export function stopFederationStatusStream(): void {
  if (source) {
    source.close();
    source = null;
  }
}
